import { DeliveryItems } from '@/api';
import { AppRoutes } from '@/app/routers';
import { useAppDispatch } from '@/app/store';
import { ReactComponent as DeliveryIcon } from '@/assets/icons/delivery.svg';

import { UIBox } from '@/components/ui/Box';
import { Typography } from '@/components/ui/Typography';

import { UIWarning } from '@/components/ui/Warning';
import {
  getDeliveryList,
  initDeliveryList,
  unlockSpecificDeliveries,
} from '@/features/deliveries/deliveriesSlice';
import { useSelector } from '@/hooks/useSelector';
import { ReceivingStatus } from '@/pages/Receiving/PageReceiving/types';
import { colors } from '@/theme';

import { SvgIcon } from '@material-ui/core';
import {
  Add,
  CalendarTodayRounded,
  Check,
  Launch,
  MoreHoriz,
  PriorityHigh,
} from '@material-ui/icons';
import { DateTime } from 'luxon';
import { FC, memo, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import {
  StyledLeftHalf,
  StyledLink,
  StyledListItem,
  StyledListItemCheckbox,
  StyledListItemDate,
  StyledListItemIcon,
  StyledListItemInfo,
  StyledListItemStatus,
  StyledListItemStatusIcon,
  StyledListItemStatusText,
  StyledListItemText,
  StyledRealItemMessage,
  StyledRightHalf,
  StyleListItemBadge,
} from './style';

export interface UIListItemProps {
  onToggle?: (delivery: DeliveryItems) => void;
  checked?: boolean;
  disabled?: boolean;
  delivery: DeliveryItems;
  onBoxesClick?: (deliveryCode: string, boxes: string[]) => void;
}

const statusTranslationKeys = {
  [ReceivingStatus.NEW]: 'receiving.status.new',
  [ReceivingStatus.PARTIAL]: 'receiving.status.partial',
  [ReceivingStatus.IN_USE]: 'receiving.status.inUse',
  [ReceivingStatus.CONFIRMED]: 'receiving.status.confirmed',
} as const;

const statusConfig = {
  [ReceivingStatus.NEW]: {
    color: colors.primary,
    icon: Add,
  },
  [ReceivingStatus.PARTIAL]: {
    color: colors.primary,
    icon: MoreHoriz,
  },
  [ReceivingStatus.IN_USE]: {
    color: colors.yellow,
    icon: PriorityHigh,
  },
  [ReceivingStatus.CONFIRMED]: {
    color: colors.green,
    icon: Check,
  },
} as const;

export const DeliveryListItem: FC<UIListItemProps> = memo(
  ({ delivery, checked, disabled, onToggle, onBoxesClick }) => {
    const history = useHistory();
    const {
      codeDelivery,
      dateShipment,
      nameUserDelivery,
      numberItems,
      status,
      soldItem,
      boxes,
    } = delivery;
    const dispatch = useAppDispatch();
    const inUse = status === ReceivingStatus.IN_USE;
    const date = DateTime.fromISO(dateShipment).toFormat('yyyy LLLL dd'); // TODO Create a Date component that format date using Intl.DateTimeFormat
    const { username } = useSelector(state => state.user);

    const { t } = useTranslation();

    const deliveryRef = useRef<HTMLDivElement>(null);
    const countRef = useRef<number>(0);
    const timerRef = useRef<number>();

    const statusLabel = inUse
      ? t('busy', { userName: nameUserDelivery ?? '' })
      : t(statusTranslationKeys[status as ReceivingStatus] || status);

    const { color: statusColor, icon: StatusIcon } =
      statusConfig[status as ReceivingStatus] ??
      statusConfig[ReceivingStatus.NEW];

    const resetDelivery = async (deliveryCodes: string[]): Promise<void> => {
      dispatch(initDeliveryList());
      await dispatch(
        unlockSpecificDeliveries({
          deliveryCodes,
        })
      );
      await dispatch(getDeliveryList());
    };

    const handleTripleClick = (): void => {
      countRef.current = countRef.current + 1;

      const timerIsPresent = timerRef.current;

      if (
        timerIsPresent &&
        countRef.current === 3 &&
        status === ReceivingStatus.IN_USE &&
        nameUserDelivery === username
      ) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
        countRef.current = 0;

        if (
          window.confirm(
            `Reset delivery ${codeDelivery} used by ${nameUserDelivery}?`
          )
        ) {
          resetDelivery([`${codeDelivery}`]);
        }
      }

      if (!timerIsPresent) {
        const timer = setTimeout(() => {
          clearTimeout(timerRef.current);
          timerRef.current = undefined;
          countRef.current = 0;
        }, 1000);

        //@ts-ignore
        timerRef.current = timer;
      }
    };

    useEffect(() => {
      if (deliveryRef.current) {
        deliveryRef.current.addEventListener('click', handleTripleClick);
      }
    });

    const isConfirmed = useMemo(() => {
      return status === ReceivingStatus.CONFIRMED;
    }, [status]);

    return (
      <StyledListItem
        ref={deliveryRef}
        button
        onClick={(): void => {
          if (disabled) {
            return;
          }

          onToggle?.(delivery);
        }}
        disabled={disabled}
        alignItems="center"
      >
        <StyledLeftHalf>
          {/* checkbox */}
          {!isConfirmed && (
            <StyledListItemIcon>
              <StyledListItemCheckbox
                edge="start"
                checked={checked}
                tabIndex={-1}
                disabled={disabled}
                disableRipple
              />
            </StyledListItemIcon>
          )}
          {/* delivery number */}
          <StyledListItemInfo $isConfirmed={isConfirmed}>
            <StyledListItemDate>
              <SvgIcon component={CalendarTodayRounded}></SvgIcon>
              <Typography size="xs" font="book">
                {date}
              </Typography>
            </StyledListItemDate>
            <StyledListItemText>
              <DeliveryIcon />
              <Typography size="lg" font="heavy">
                {codeDelivery}
              </Typography>
            </StyledListItemText>
          </StyledListItemInfo>
          {/* boxes */}
          <StyledLink
            onClick={(
              event: React.MouseEvent<HTMLSpanElement, MouseEvent>
            ): void => {
              if (!disabled && boxes && boxes.length > 0) {
                event.stopPropagation();
                onBoxesClick?.(codeDelivery, boxes ?? []);
              }
            }}
          >
            {t('receiving.boxes', { count: boxes?.length ?? 0 })}{' '}
          </StyledLink>
        </StyledLeftHalf>
        <StyledRightHalf>
          <StyledRealItemMessage>
            {soldItem && !isConfirmed && (
              <UIWarning label={t('receiving.soldItems')} />
            )}
          </StyledRealItemMessage>
          <StyleListItemBadge>
            <Typography size="xs" color="white">
              {numberItems} items
            </Typography>
          </StyleListItemBadge>
          <UIBox ml="auto" alignItems="center">
            {isConfirmed && (
              <Launch
                color={'primary'}
                onClick={(): void =>
                  history.push(
                    `${AppRoutes.RECEIVING_DELIVERIES}/${codeDelivery}`
                  )
                }
              />
            )}
          </UIBox>
          <StyledListItemStatus>
            <StyledListItemStatusText size="lg" font="medium">
              {statusLabel}
            </StyledListItemStatusText>
            <StyledListItemStatusIcon $backgroundColor={statusColor}>
              <StatusIcon />
            </StyledListItemStatusIcon>
          </StyledListItemStatus>
        </StyledRightHalf>
      </StyledListItem>
    );
  }
);

DeliveryListItem.displayName = 'DeliveryListItem';

export default DeliveryListItem;
